import { useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NAP } from '../config/nap';
import { LANGS, OG_LOCALE, isRtl, localizedPath, hreflangSet } from '../config/langs';
import { publishAlternates } from './pageAlternates';
import { organizationNode } from '../config/schema';

const SITE_URL = NAP.url;
const SITE_NAME = NAP.name.en;
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

/**
 * SEO component using React 19 native metadata.
 * All <title>, <meta>, <link> children are auto-hoisted to <head>.
 *
 * Props:
 *   title       — page title; the brand is appended unless `fullTitle` is set
 *   fullTitle   — use this string as the <title> verbatim, no brand suffix
 *   description — meta description (150-160 chars)
 *   path        — the page path WITHOUT a language prefix, e.g. "/project/123".
 *                 The prefix for the current language is added here.
 *   langs       — the languages this page exists in (default: all). On a
 *                 language outside this list the page is a fallback render,
 *                 so it is noindexed (and, like every noindex page, carries
 *                 no canonical).
 *   alternates  — { lang: path } when the path differs per language
 *                 (blog posts). Defaults to `path` for every language.
 *   image, type, noindex, keywords, author, publishedAt, updatedAt
 *
 * The prerenderer (scripts/prerender.mjs) writes the same tags into the
 * static HTML from the same config modules; main.jsx removes those copies
 * before this component mounts.
 */
export default function SEO({
    title,
    fullTitle: fullTitleProp,
    description,
    path = '/',
    langs = LANGS,
    alternates,
    image = DEFAULT_IMAGE,
    type = 'website',
    noindex = false,
    keywords,
    author,
    publishedAt,
    updatedAt
}) {
    const { i18n } = useTranslation();
    const lang = LANGS.includes((i18n.language || '').split('-')[0]) ? i18n.language.split('-')[0] : 'ar';

    const cleanPath = String(path).split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    const pathFor = (l) => (alternates && alternates[l]) || cleanPath;
    const available = LANGS.filter((l) => langs.includes(l) && (!alternates || alternates[l]));
    const existsHere = available.includes(lang);

    // Canonical is built from the path alone — never from window.location — so
    // ?utm_*, ?fbclid and every other query variant collapse onto one URL.
    const canonicalLang = existsHere ? lang : (available.includes('ar') ? 'ar' : available[0] || 'ar');
    const canonical = `${SITE_URL}${localizedPath(pathFor(canonicalLang), canonicalLang)}`;
    const hreflangs = hreflangSet(SITE_URL, '/', available).map((h) => ({
        ...h,
        href: `${SITE_URL}${localizedPath(pathFor(h.lang), h.lang)}`
    }));
    const robotsNoindex = noindex || !existsHere;

    const brand = NAP.name[lang] || SITE_NAME;
    const computedTitle = fullTitleProp || (title ? `${title} | ${brand}` : `${NAP.name.ar} | ${SITE_NAME}`);
    const desc =
        description ||
        (lang === 'ar'
            ? 'عقارات دبي وأبوظبي على الخارطة بأسعار المطوّر مباشرة وبدون عمولة. وسيط مرخّص من دائرة الأراضي والأملاك بدبي.'
            : 'Off-plan real estate in Dubai and Abu Dhabi at direct developer prices, zero commission. Licensed by the Dubai Land Department.');

    // The prerendered <head> tags (title, description, canonical, hreflang,
    // robots, JSON-LD) carry data-pr. This component emits the same set, so the
    // static copies go — otherwise the page ends up with two canonicals and
    // two JSON-LD graphs after hydration. Pages without an SEO component
    // (privacy, terms, account screens) keep their prerendered tags.
    useLayoutEffect(() => {
        document.querySelectorAll('head [data-pr]').forEach((el) => el.remove());
    }, []);

    // Set html lang + dir attributes dynamically (SEO signal + accessibility)
    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr';
    }, [lang]);

    // Tell the language switcher where each language version of this page is.
    const alternatesKey = available.map((l) => `${l}:${pathFor(l)}`).join('|');
    useEffect(() => {
        publishAlternates(Object.fromEntries(available.map((l) => [l, pathFor(l)])));
        return () => publishAlternates(null);
        // alternatesKey captures every input of the published map.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alternatesKey]);

    const robots = robotsNoindex
        ? 'noindex,follow'
        : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

    return (
        <>
            <title>{computedTitle}</title>
            {/* The company node, on every page — the same one the prerendered
                HTML carries. Other blocks point at it by @id. */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationNode(lang)) }} />
            <meta name="description" content={desc} />
            {keywords && <meta name="keywords" content={keywords} />}
            <meta name="robots" content={robots} />

            {/* No canonical on a noindex page — the same rule as the static
                HTML (scripts/lib/html.mjs). noindex plus a canonical are two
                signals that contradict each other. */}
            {!robotsNoindex && <link rel="canonical" href={canonical} />}
            {/* Reciprocal hreflang across every language version, plus
                x-default. Only languages the page really exists in are listed;
                an alternate pointing at a fallback render would invalidate the
                whole set. */}
            {!robotsNoindex && hreflangs.map((h) => (
                <link key={h.hreflang} rel="alternate" hrefLang={h.hreflang} href={h.href} />
            ))}

            <meta property="og:locale" content={OG_LOCALE[lang] || 'ar_AE'} />
            {available
                .filter((l) => l !== lang)
                .map((l) => (
                    <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
                ))}

            {/* Open Graph — Facebook / LinkedIn / WhatsApp */}
            <meta property="og:title" content={computedTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={canonical} />
            <meta property="og:type" content={type} />
            <meta property="og:image" content={image} />
            <meta property="og:site_name" content={SITE_NAME} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={computedTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={image} />

            {/* Entity consistency — the same NAP the footer and JSON-LD state. */}
            <meta name="geo.region" content="AE-DU" />
            <meta name="geo.placename" content={NAP.address.locality.en} />
            <meta name="business:contact_data:phone_number" content={NAP.phone} />

            {/* Article-specific meta */}
            {type === 'article' && author && <meta property="article:author" content={author} />}
            {type === 'article' && publishedAt && <meta property="article:published_time" content={publishedAt} />}
            {type === 'article' && updatedAt && <meta property="article:modified_time" content={updatedAt} />}
        </>
    );
}
